import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
    Paper, Typography, Box, IconButton, Alert, Accordion, AccordionSummary, AccordionDetails, Skeleton
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@awesome.me/kit-e6334c76d4/icons/duotone/solid';
import { apiFetch } from '../services/api';
import { useOri } from '../context/OriContext';
import SubmissionDetails, { SubmissionDetail } from './SubmissionDetails';
import SrfIcons from './SrfIcons';
import EbtsView, { EbtsSummary } from './EbtsView';
import type { Submission } from './SubmissionsList';
import LoadingIcon from './LoadingIcon';

interface LocationState {
    from?: 'dashboard' | 'submissions';
    ori?: string;
    submission?: Submission;
}

/**
 * Lazily loaded parsed-EBTS section (submission or response transaction).
 */
const EbtsSection: React.FC<{ title: string; url: string }> = ({ title, url }) => {
    const [summary, setSummary] = useState<EbtsSummary | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await apiFetch(url);
            if (!response.ok) {
                const data = await response.json().catch(() => null);
                throw new Error(data?.error || 'Failed to load the transaction file.');
            }
            setSummary(await response.json());
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setIsLoading(false);
        }
    }, [url]);

    const handleChange = (_: unknown, isExpanded: boolean) => {
        setExpanded(isExpanded);
        if (isExpanded && !summary && !isLoading) {
            load();
        }
    };

    return (
        <Accordion expanded={expanded} onChange={handleChange} disableGutters>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {isLoading && <Skeleton variant="rectangular" height={160} />}
                {error && <Alert severity="error">{error}</Alert>}
                {summary && <EbtsView summary={summary} />}
            </AccordionDetails>
        </Accordion>
    );
};

const SubmissionDetailsPage: React.FC = () => {
    const { tcn } = useParams<{ tcn: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { selectedOri } = useOri();
    const state = (location.state ?? {}) as LocationState;

    const ori = state.submission?.ori || state.ori || selectedOri || '';
    const backTarget = state.from === 'dashboard' ? '/' : '/submissions';

    const [details, setDetails] = useState<SubmissionDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!tcn) return;
        let cancelled = false;
        (async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await apiFetch(`/api/risc/sub/${tcn}?ori=${ori}`);
                if (!response.ok) {
                    const data = await response.json().catch(() => null);
                    throw new Error(data?.error || 'Failed to load submission details.');
                }
                const data: SubmissionDetail = await response.json();
                if (!cancelled) setDetails(data);
            } catch (err) {
                if (!cancelled) setError(err instanceof Error ? err.message : 'An unknown error occurred.');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [tcn, ori]);

    if (!tcn) {
        return <Alert severity="error" sx={{ m: 2 }}>No transaction specified.</Alert>;
    }

    const sub = state.submission;
    const isComplete = details?.state === 'RESPONSE_RECEIVED' || sub?.state === 'COMPLETE';

    // A minimal Submission shape when deep-linked without router state.
    const submissionForDetails: Submission = sub ?? ({
        tcn,
        ori,
        state: isComplete ? 'COMPLETE' : 'PENDING',
        stateSrf: details?.stateResponse?.srf ?? '',
        fbiSrf: details?.fbiResponse?.srf ?? '',
        stateCaseType: details?.stateResponse?.['case-type'] ?? '',
    } as Submission);

    return (
        <Box sx={{ p: 2 }}>
            <LoadingIcon isLoading={isLoading} showOverlay={true} />
            <Paper sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={1} flexWrap="wrap">
                    <IconButton onClick={() => navigate(backTarget)} title={state.from === 'dashboard' ? 'Back to Dashboard' : 'Back to Submissions'}>
                        <FontAwesomeIcon icon={faArrowLeft} swapOpacity />
                    </IconButton>
                    <Typography variant="h6" component="h1" className="mono-id" sx={{ fontFamily: 'var(--font-mono)' }}>
                        {tcn}
                    </Typography>
                    <span className={`status ${isComplete ? 'status-complete' : 'status-pending'}`}>
                        {isComplete ? 'Complete' : 'Pending'}
                    </span>
                    {isComplete && (
                        <SrfIcons
                            fbiSrf={sub?.fbiSrf ?? details?.fbiResponse?.srf ?? ''}
                            stateSrf={sub?.stateSrf ?? details?.stateResponse?.srf ?? ''}
                            stateCaseType={sub?.stateCaseType ?? details?.stateResponse?.['case-type'] ?? ''}
                        />
                    )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 6 }}>
                    Agency <span className="mono-id">{ori}</span>
                    {sub?.submittedBy && <> · submitted by {sub.submittedBy}</>}
                    {sub?.created && <> · {new Date(sub.created).toLocaleString()}</>}
                    {sub?.device && <> · device <span className="mono-id">{sub.device}</span></>}
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {details && <SubmissionDetails details={details} submission={submissionForDetails} />}
            </Paper>

            <Box mt={2}>
                <EbtsSection title="Submission EBTS" url={`/api/risc/sub/${tcn}/ebts?ori=${ori}`} />
                {isComplete && (
                    <EbtsSection title="Response EBTS" url={`/api/risc/sub/${tcn}/ebts/response?ori=${ori}`} />
                )}
            </Box>
        </Box>
    );
};

export default SubmissionDetailsPage;
